/* ===========================================================================
   mm-anim.js — scroll reveal + scroll chrome, for every template.

   Three things this theme does that a naive reveal script gets wrong:

   1. The window does not scroll. html/body are overflow:hidden and the real
      scroller is .page-wrapper, so window 'scroll' events never fire. We use a
      CAPTURE listener on document, which catches scroll from any element, and
      read progress from whichever element actually scrolled.

   2. At DOMContentLoaded, images have no height yet, so the page is collapsed
      to roughly one screen and EVERY element looks on-screen. Revealing then
      fires the whole page at once. So we arm elements early (to get the hidden
      state in place before paint) but only start observing after load, once
      layout is stable.

   3. Hidden states must never use clip-path/height/visibility. An
      IntersectionObserver measures clipped geometry, so hiding that way makes
      an element report 0% visible forever and the reveal can never fire — the
      exact deadlock that made the previous motion layer eat the images.

   Safety: the hidden CSS is gated on html.mm-anim-on, which an inline head
   script sets with a 3s self-destruct that this file disarms on boot. If this
   file never runs, nothing stays hidden.
   =========================================================================== */
(() => {
  if (window.__mmAnim) return;
  window.__mmAnim = true;

  const root = document.documentElement;
  clearTimeout(window.__mmAnimFallback);
  root.classList.add('mm-anim-on');

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MARGIN = 160;
  const MAX_STAGGER = 5;
  const STEP = 70;

  const SKIP_INSIDE = [
    'header', '#header-group', 'footer nav', 'dialog', '[popover]',
    '.cart-drawer', '.theme-drawer', '.mm-shell-header', '.shopify-section-header',
    '[class*="slideshow"]', '[class*="carousel"]', '[class*="marquee"]',
    '[class*="gallery"]', 'media-gallery', 'scroll-container', 'slideshow-component',
    '[class*="sticky"]', '[class*="drawer"]', '[class*="modal"]', '[class*="popover"]',
  ].join(',');

  const CANDIDATES = [
    'section > *', '[class*="card"]', '[class*="__image"]',
    '[class*="__media"]', '[class*="__tile"]', 'h1', 'h2', '.mm-h2',
  ].join(',');

  const MEDIA_HINT = /(image|media|tile|photo|picture|thumb|banner|hero)/i;

  /* --- Reveal bookkeeping ------------------------------------------------ */
  const pending = new Set();
  let observer = null;
  let live = false;   // true once layout is stable and we may start revealing

  const show = (el) => {
    if (!el || el.dataset.mmIn === '1') return;
    el.dataset.mmIn = '1';
    el.classList.add('mm-in');
    pending.delete(el);
    if (observer) observer.unobserve(el);
  };
  const showAll = () => { for (const el of [...pending]) show(el); };

  const near = (el) => {
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return true;   // unmeasurable -> show, never hide
    return r.top < innerHeight + MARGIN && r.bottom > -MARGIN;
  };
  const sweep = () => { if (live) for (const el of [...pending]) if (near(el)) show(el); };

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => { if (live) entries.forEach((e) => { if (e.isIntersecting) show(e.target); }); },
      { threshold: 0, rootMargin: `${MARGIN}px 0px ${MARGIN}px` }
    );
  }

  const isAnimatable = (el) => {
    if (!el || el.dataset.mmR) return false;
    if (el.closest(SKIP_INSIDE)) return false;
    const r = el.getBoundingClientRect();
    if (r.width < 24) return false;
    if (r.height > innerHeight * 3) return false;          // page-sized wrappers
    const pos = getComputedStyle(el).position;
    if (pos === 'fixed' || pos === 'sticky') return false; // never move chrome
    return true;
  };

  const arm = (el, index) => {
    el.dataset.mmR = '1';
    const media = MEDIA_HINT.test(el.className || '') || el.tagName === 'IMG'
      || !!el.querySelector(':scope > img, :scope > picture');
    el.classList.add(media ? 'mm-r-media' : 'mm-r');
    el.style.setProperty('--mm-r-delay', `${Math.min(index, MAX_STAGGER) * STEP}ms`);
    if (reduced) { show(el); return; }
    pending.add(el);
    if (live && observer) observer.observe(el);
  };

  const scan = (scope) => {
    const target = scope && scope.querySelectorAll ? scope : document;
    const main = document.querySelector('#MainContent') || document.body;

    const picked = [];
    target.querySelectorAll(CANDIDATES).forEach((el) => {
      if (!main.contains(el) || !isAnimatable(el)) return;
      // Skip when an ancestor already animates — avoids nested double motion.
      if (el.parentElement && el.parentElement.closest('[data-mm-r="1"]')) return;
      picked.push(el);
    });

    let group = null;
    let index = 0;
    picked.forEach((el) => {
      if (el.parentElement !== group) { group = el.parentElement; index = 0; }
      arm(el, index++);
    });

    target.querySelectorAll('[class*="card"], [class*="__tile"]').forEach((el) => {
      if (el.closest(SKIP_INSIDE)) return;
      el.classList.add('mm-hover-lift');
      if (el.querySelector('img')) el.classList.add('mm-zoom');
    });

    sweep();
  };

  /* --- Go live only once layout has settled ------------------------------ */
  const goLive = () => {
    if (live) return;
    live = true;
    if (observer) for (const el of pending) observer.observe(el);
    sweep();
    let ticks = 0;
    const settle = setInterval(() => { sweep(); if (++ticks > 8) clearInterval(settle); }, 250);
  };

  const boot = () => {
    try { scan(document); } catch (err) { showAll(); return; }
    if (document.readyState === 'complete') requestAnimationFrame(goLive);
    else addEventListener('load', () => requestAnimationFrame(goLive), { once: true });
    // If load drags (a slow image), go live anyway rather than hold content back.
    setTimeout(goLive, 4000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else boot();

  document.addEventListener('shopify:section:load', (e) => { live = true; scan(e.target); });
  addEventListener('pageshow', () => { goLive(); scan(document); });
  addEventListener('popstate', () => setTimeout(() => { goLive(); scan(document); }, 150));

  /* --- Scroll chrome, bound to whatever actually scrolls ------------------ */
  const bar = document.createElement('div');
  bar.className = 'mm-progress';
  bar.setAttribute('aria-hidden', 'true');
  (document.body || root).append(bar);

  const pickScroller = () => {
    const wrap = document.querySelector('.page-wrapper');
    if (wrap && wrap.scrollHeight > wrap.clientHeight + 20) return wrap;
    const se = document.scrollingElement || root;
    if (se.scrollHeight > se.clientHeight + 20) return se;
    return null;
  };
  let scroller = pickScroller();

  let ticking = false;
  const onFrame = () => {
    ticking = false;
    if (!scroller || !scroller.isConnected) scroller = pickScroller();
    const el = scroller;
    const top = el ? el.scrollTop : 0;
    const max = el ? Math.max(1, el.scrollHeight - el.clientHeight) : 1;
    root.style.setProperty('--mm-progress', String(Math.min(1, top / max)));
    root.classList.toggle('mm-scrolled', top > 24);
  };
  const onScroll = () => {
    sweep();
    if (!ticking) { ticking = true; requestAnimationFrame(onFrame); }
  };

  // Capture phase catches scroll from ANY element, including .page-wrapper.
  document.addEventListener('scroll', (e) => {
    const t = e.target === document ? (document.scrollingElement || root) : e.target;
    if (t && t.nodeType === 1 && t.scrollHeight > t.clientHeight + 20) scroller = t;
    onScroll();
  }, { passive: true, capture: true });
  addEventListener('resize', () => { scroller = pickScroller(); onScroll(); }, { passive: true });
  onFrame();
})();
